/**
 * Deterministic settlement binding repair.
 *
 * A resident owns a stable facility id, but sleeps and works at replaceable
 * physical furniture pids. These helpers derive legal replacements from the
 * current room solve; clients can preview them and the server re-derives the
 * exact same set before committing an intent.
 */
import { homesteadWalkPath, type HomesteadRoomSolution, type RoomFunction } from "../homestead/rooms.js";
import type { PlacedPiece } from "../homestead/types.js";
import {
  cloneSettlementState,
  settlementFacilityCapacity,
  settlementFacilityVariant,
  type SettlementFacilityV1,
  type SettlementResidentV1,
  type SettlementStateV1,
} from "./production.js";
import { settlementVariantAcceptsProfession } from "./model.js";

export interface SettlementResidentAssignmentOption {
  homePid: number;
  workplacePid: number;
  facilityId: string;
  facilityAnchorPid: number;
  roomFunction: RoomFunction;
}

export interface SettlementFacilityRelicenseTarget {
  anchorPid: number;
  roomFunction: RoomFunction;
  workstationPids: number[];
}

export interface SettlementAssignmentResult {
  ok: boolean;
  state: SettlementStateV1;
  err?: string;
}

function byId<T extends { id: string }>(a: T, b: T): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function roomForFacility(solution: HomesteadRoomSolution, facility: SettlementFacilityV1) {
  return solution.rooms.find((room) =>
    room.functionAnchors[facility.roomFunction] === facility.anchorPid);
}

/** Resolve a pre-v47 resident's facility without allowing ambiguous guesses. */
export function settlementResidentFacilityId(state: SettlementStateV1,
  resident: SettlementResidentV1, solution: HomesteadRoomSolution): string | null {
  const explicit = resident.facilityId === undefined ? undefined
    : state.facilities.find((facility) => facility.id === resident.facilityId
      && settlementVariantAcceptsProfession(settlementFacilityVariant(facility), resident.profession));
  if (explicit) return explicit.id;
  if (resident.workplacePid !== null) {
    const physical = state.facilities.filter((facility) =>
      settlementVariantAcceptsProfession(settlementFacilityVariant(facility), resident.profession)
      && (roomForFacility(solution, facility)?.functionSlots[facility.roomFunction]
        .includes(resident.workplacePid!) ?? false));
    if (physical.length === 1) return physical[0]!.id;
  }
  const sameProfession = state.facilities.filter((facility) =>
    settlementVariantAcceptsProfession(settlementFacilityVariant(facility), resident.profession));
  return sameProfession.length === 1 ? sameProfession[0]!.id : null;
}

function assignedResidents(state: SettlementStateV1, facility: SettlementFacilityV1,
  solution: HomesteadRoomSolution): SettlementResidentV1[] {
  const workerIds = new Set(facility.queue.flatMap((job) => job.workerIds));
  return state.residents.filter((resident) => resident.facilityId === facility.id
    || workerIds.has(resident.id)
    || (resident.facilityId === undefined
      && settlementResidentFacilityId(state, resident, solution) === facility.id)).sort(byId);
}

/** Every vacant, reachable bed/station pair legal for one resident. */
export function settlementResidentAssignmentOptions(state: SettlementStateV1, residentId: string,
  solution: HomesteadRoomSolution, pieces: readonly PlacedPiece[]): SettlementResidentAssignmentOption[] {
  const resident = state.residents.find((candidate) => candidate.id === residentId);
  if (!resident || resident.profession === null) return [];
  const occupiedHomes = new Set(state.residents
    .filter((candidate) => candidate.id !== residentId && candidate.homePid !== null)
    .map((candidate) => candidate.homePid!));
  const occupiedWorkplaces = new Set(state.residents
    .filter((candidate) => candidate.id !== residentId && candidate.workplacePid !== null)
    .map((candidate) => candidate.workplacePid!));
  const pieceByPid = new Map(pieces.map((piece) => [piece.pid, piece]));
  const beds = solution.rooms.flatMap((room) => room.functionSlots.dwelling)
    .filter((pid) => !occupiedHomes.has(pid) && pieceByPid.has(pid));
  const out: SettlementResidentAssignmentOption[] = [];
  for (const facility of state.facilities.filter((candidate) =>
    settlementVariantAcceptsProfession(settlementFacilityVariant(candidate), resident.profession)).sort(byId)) {
    const room = roomForFacility(solution, facility);
    if (!room) continue;
    const stations = room.functionSlots[facility.roomFunction]
      .slice(0, settlementFacilityCapacity(facility).workerSlots)
      .filter((pid) => !occupiedWorkplaces.has(pid) && pieceByPid.has(pid));
    for (const homePid of beds) for (const workplacePid of stations) {
      const home = pieceByPid.get(homePid)!;
      const work = pieceByPid.get(workplacePid)!;
      if (!homesteadWalkPath(solution, home.p, work.p)) continue;
      out.push({
        homePid, workplacePid, facilityId: facility.id,
        facilityAnchorPid: facility.anchorPid, roomFunction: facility.roomFunction,
      });
    }
  }
  return out.sort((a, b) => a.homePid - b.homePid || a.facilityId.localeCompare(b.facilityId)
    || a.workplacePid - b.workplacePid);
}

/** Recognized same-function rooms with enough unclaimed physical stations. */
export function settlementFacilityRelicenseTargets(state: SettlementStateV1, facilityId: string,
  solution: HomesteadRoomSolution, pieces: readonly PlacedPiece[]): SettlementFacilityRelicenseTarget[] {
  const facility = state.facilities.find((candidate) => candidate.id === facilityId);
  if (!facility) return [];
  const crew = assignedResidents(state, facility, solution);
  const crewIds = new Set(crew.map((resident) => resident.id));
  const occupiedByOthers = new Set(state.residents
    .filter((resident) => !crewIds.has(resident.id) && resident.workplacePid !== null)
    .map((resident) => resident.workplacePid!));
  const claimedAnchors = new Set(state.facilities
    .filter((candidate) => candidate.id !== facility.id)
    .map((candidate) => `${candidate.roomFunction}:${candidate.anchorPid}`));
  return solution.rooms.flatMap((room): SettlementFacilityRelicenseTarget[] => {
    const anchorPid = room.functionAnchors[facility.roomFunction];
    if (anchorPid === undefined || anchorPid === facility.anchorPid
      || claimedAnchors.has(`${facility.roomFunction}:${anchorPid}`)) return [];
    const workstationPids = room.functionSlots[facility.roomFunction]
      .slice(0, settlementFacilityCapacity(facility).workerSlots)
      .filter((pid) => !occupiedByOthers.has(pid));
    return workstationPids.length >= crew.length
      && stationAssignment(crew, workstationPids, solution, pieces) !== null
      ? [{ anchorPid, roomFunction: facility.roomFunction, workstationPids }]
      : [];
  }).sort((a, b) => a.anchorPid - b.anchorPid);
}

function stationAssignment(crew: readonly SettlementResidentV1[], workstationPids: readonly number[],
  solution: HomesteadRoomSolution, pieces: readonly PlacedPiece[]): Map<string, number> | null {
  const pieceByPid = new Map(pieces.map((piece) => [piece.pid, piece]));
  const candidates = new Map<string, number[]>();
  for (const resident of crew) {
    const home = resident.homePid === null ? undefined : pieceByPid.get(resident.homePid);
    const reachable = workstationPids.filter((pid) => {
      const work = pieceByPid.get(pid);
      return !!work && (!home || !!homesteadWalkPath(solution, home.p, work.p));
    });
    if (reachable.length === 0) return null;
    candidates.set(resident.id, reachable);
  }
  // Maximum four workers: deterministic DFS produces the lexicographically
  // first complete one-to-one assignment rather than depending on array order.
  const assigned = new Map<string, number>();
  const used = new Set<number>();
  const match = (at: number): boolean => {
    if (at >= crew.length) return true;
    const resident = crew[at]!;
    for (const pid of candidates.get(resident.id) ?? []) {
      if (used.has(pid)) continue;
      used.add(pid); assigned.set(resident.id, pid);
      if (match(at + 1)) return true;
      assigned.delete(resident.id); used.delete(pid);
    }
    return false;
  };
  return match(0) ? assigned : null;
}

/** Move one stable facility license and deterministically remap its whole crew. */
export function relicenseSettlementFacility(input: SettlementStateV1, facilityId: string,
  targetAnchorPid: number, solution: HomesteadRoomSolution, pieces: readonly PlacedPiece[],
  nowMs: number): SettlementAssignmentResult {
  const state = cloneSettlementState(input);
  const facility = state.facilities.find((candidate) => candidate.id === facilityId);
  if (!facility) return { ok: false, state, err: "FACILITY NOT FOUND" };
  const target = settlementFacilityRelicenseTargets(state, facilityId, solution, pieces)
    .find((candidate) => candidate.anchorPid === targetAnchorPid);
  if (!target) return { ok: false, state, err: "FACILITY LOCATION IS NO LONGER AVAILABLE" };

  const crew = assignedResidents(state, facility, solution);
  const assigned = stationAssignment(crew, target.workstationPids, solution, pieces);
  if (!assigned) return { ok: false, state, err: "FACILITY NEEDS MORE REACHABLE WORKSTATIONS" };

  facility.anchorPid = target.anchorPid;
  facility.lastEvaluatedAt = Math.max(0, Math.floor(nowMs));
  facility.rateRemainder = 0;
  for (const resident of crew) {
    resident.facilityId = facility.id;
    resident.workplacePid = assigned.get(resident.id)!;
  }
  state.updatedAt = facility.lastEvaluatedAt;
  return { ok: true, state };
}
